var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as L from 'leaflet';
import uniqueId from 'lodash/uniqueId';
import identity from 'lodash/identity';
import { key2tile, tile2key } from './quadkeys';
import queryString from 'query-string';
import axios from 'axios';
import { DateTime } from 'luxon';
import { ControlGridAPIError } from './ControlGridAPIError';
import { ControlLegendColorstep } from './ControlLegendColorstep';
import { findClosestIndex } from './lib/utils/array';
import { TIME_CHANGED, TIME_PLAYBACK_TICKED } from './events';
/**
 * The Grid API date format.
 */
const GRIDAPI_DATE_FORMAT = 'yyyy-MM-dd HH:mm';
/**
 * The GridAPI layer's default options.
 */
const LAYER_GRID_API_OPTIONS_DEFAULT = {
    errors: {
        noTiles: 'The server sent an empty list of tiles.',
        noTilesAtIndex: 'There are no tiles for this time index.',
        connectionError: 'There was an error with the connection to the server.',
    },
    tileSize: 256,
    cacheMaxImages: 2000,
    scaleTilesZoomGeq: null,
    hideBeforeZoom: null,
    hideAfterZoom: null,
    init: null,
};
/**
 * The GridAPI layer.
 */
export class LayerGridAPI extends L.GridLayer {
    /**
     * Create a new GridAPI layer instance.
     * @param options - The GridAPI layer options.
     * @param maapInstance - The M.A.A.P. widget.
     * @see {@link https://gridapi.meteotest.ch/map_services|WMTS services}
     * @see {@link https://gridapi.meteotest.ch/colormaps|Colormaps for legends}
     */
    constructor(options, maapInstance) {
        super(Object.assign(Object.assign({}, LAYER_GRID_API_OPTIONS_DEFAULT), options));
        /**
         * Time steps
         */
        this.timesteps = [];
        /**
         * Indicates if a time step is a forecast
         */
        this.forecast = [];
        /**
         * the currently active time
         */
        this.time = null;
        if (options.blendMode &&
            !window.getComputedStyle(document.body).getPropertyValue('mix-blend-mode')) {
            options.opacity = options.opacity || 0.5;
            delete options.blendMode;
        }
        this.maapInstance = maapInstance;
        this.urlSchemes = [];
        this.images = {
            loading: {},
            loaded: {},
            failed: {},
        };
        if (this.options.legend) {
            this.legend = new ControlLegendColorstep({
                name: uniqueId('colorstep-' + this.options.wmtsName),
                wmtsName: this.options.wmtsName,
                baseUri: this.options.baseUrl,
                title: this.options.legend.title,
                unitKey: this.options.legend.unitKey,
                formatter: this.options.legend.formatter || identity,
                interval: this.options.legend.interval,
                horizontal: this.options.legend.horizontal,
            });
        }
        this.maapInstance.on(`${TIME_PLAYBACK_TICKED} ${TIME_CHANGED}`, () => {
            const dtCurrent = this.maapInstance.time.currentTime();
            if (!dtCurrent)
                return;
            this.setTime(dtCurrent);
        });
    }
    /**
     * The hook to execute when the control is added to the map.
     * @param map - The Leaflet map the layer is added to.
     * @returns The GridAPI layer.
     * @see {@link https://leafletjs.com/reference-1.7.1.html#layer-extension-methods}
     */
    onAdd(map) {
        L.GridLayer.prototype.onAdd.call(this, map);
        if (this.options.blendMode) {
            const container = this.getContainer();
            if (container) {
                container.style.mixBlendMode = this.options.blendMode;
            }
        }
        if (this.legend) {
            this.maapInstance.controls.addControl(this.legend);
        }
        this.error = new ControlGridAPIError({
            name: uniqueId('gridapi-error-' + this.options.wmtsName),
        });
        this.maapInstance.controls.addControl(this.error);
        this.error.addCustomMarkup();
        this._fetchData().then(() => {
            let initialIndex = 0;
            const currentTime = this.maapInstance.time.currentTime();
            if (this.options.initialStep) {
                const dtInit = DateTime.fromISO(this.options.initialStep, {
                    zone: 'utc',
                });
                initialIndex = this.timesteps.findIndex((dt) => dt.equals(dtInit));
                if (initialIndex < 0) {
                    throw new Error(`initialStep ${this.options.initialStep} not found in available time steps`);
                }
            }
            else if (currentTime === undefined) {
                // initially, jump to latest non-forecast step
                for (let i = 0; i < this.forecast.length; i++) {
                    if (this.forecast[i]) {
                        break;
                    }
                    else {
                        initialIndex = i;
                    }
                }
            }
            else {
                // jump to step that is closest to previously selected step
                initialIndex = this.getClosestIndex(currentTime);
            }
            this.maapInstance.setTimesteps(this.timesteps, initialIndex);
            this.preloadSchemes();
            this.maapInstance.layers.on('LAYERS_LAYER_SHOWN', ({ layer }) => {
                const currentTime = this.maapInstance.time.currentTime();
                if (currentTime && layer.options.name === this.options.name) {
                    this.maapInstance.setTimesteps(this.timesteps, this.getClosestIndex(currentTime));
                }
            });
        });
        return this;
    }
    /**
     * The hook to execute when the control is removed from the map.
     * @param map - The Leaflet map the layer is removed from.
     */
    onRemove(map) {
        if (this.error) {
            this.maapInstance.controls.removeControl(this.error.options.name);
        }
        if (this.legend) {
            this.maapInstance.controls.removeControl(this.legend.options.name);
        }
        L.GridLayer.prototype.onRemove.call(this, map);
        return this;
    }
    /**
     * Fetch the GridAPI data.
     */
    _fetchData() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.options.baseUrl) {
                throw new Error('Argument "baseUrl" is missing.');
            }
            if (!this.options.wmtsName || this.options.wmtsName.length === 0) {
                throw new Error('Argument "wmtsName" is missing.');
            }
            // build URL
            const params = {};
            params.start = DateTime.fromISO(this.options.start, {
                zone: 'utc',
            }).toFormat(GRIDAPI_DATE_FORMAT);
            params.end = DateTime.fromISO(this.options.end, { zone: 'utc' }).toFormat(GRIDAPI_DATE_FORMAT);
            if (this.options.init !== null) {
                params.init = DateTime.fromISO(this.options.init, {
                    zone: 'utc',
                }).toFormat(GRIDAPI_DATE_FORMAT);
            }
            let uri = `${this.options.baseUrl}wmts/${this.options.wmtsName}/list_tile_urls`;
            uri += '?' + queryString.stringify(params);
            try {
                const res = yield axios.get(uri);
                this.parseReponse(res.data);
            }
            catch (err) {
                if (this.error) {
                    this.error.setMessage(this.options.errors.connectionError);
                }
            }
        });
    }
    /**
     * Extract data from the GridAPI response.
     * @param json - The GridAPI JSON Response.
     */
    parseReponse(json) {
        const timesteps = [];
        const forecast = [];
        if (this.error) {
            if (json.tiles.length === 0) {
                this.error.setMessage(this.options.errors.noTiles);
            }
            else {
                this.error.setMessage(null);
            }
        }
        json.tiles.forEach((tile) => {
            this.urlSchemes.push(tile.url);
            timesteps.push(DateTime.fromISO(tile.isotime, { zone: 'utc' }));
            forecast.push(tile.forecast);
        });
        this.timesteps = timesteps;
        this.forecast = forecast;
    }
    /**
     * Set the layer's time.
     * @param time - The time to set.
     */
    setTime(time) {
        this.time = time;
        if (this.container) {
            this.container.style.display = 'none';
        }
        if (!this._map || this.timesteps.length === 0) {
            return;
        }
        // loop over canvas elements, i.e., tiles
        Object.keys(this._tiles).forEach((key) => {
            const tile = this._tiles[key];
            this.drawContent(tile.el, tile.coords);
        });
        if (this.container) {
            this.container.style.display = 'block';
        }
    }
    /**
     * Get the index of the timestep closest to the selected timestep.
     * @param currentTime - The currently selected timestep.
     * @returns The index of the timestep closest to the selected timestep
     */
    getClosestIndex(currentTime) {
        return findClosestIndex(currentTime, this.timesteps, (dt1, dt2) => {
            return Math.abs(dt1.diff(dt2).toMillis());
        });
    }
    /**
     * Load images for all time steps into memory
     * @private
     */
    preloadSchemes() {
        // make sure number of memory cached images is not unbounded
        const cache_length = Object.keys(this.images.loaded).length;
        if (cache_length > this.options.cacheMaxImages) {
            this.images.loaded = {};
        }
        this.urlSchemes.forEach((scheme) => {
            this.loadScheme(scheme, () => null);
        });
    }
    /**
     * Load all images of a URL scheme / time step into memory.
     * @param scheme - The scheme to laod.
     * @param callback - The callback to load the tile.
     */
    loadScheme(scheme, callback) {
        Object.keys(this._tiles).forEach((key) => {
            const tile = this._tiles[key];
            const coords = tile.coords;
            const zoom = coords.z;
            if (this.options.hideBeforeZoom !== null &&
                zoom < this.options.hideBeforeZoom) {
                return;
            }
            if (this.options.hideAfterZoom !== null &&
                zoom > this.options.hideAfterZoom) {
                return;
            }
            let quadkey = tile2key(coords.x, coords.y, coords.z);
            // check if client side scaling is active for this zoom level...
            if (this.options.scaleTilesZoomGeq !== null &&
                zoom >= this.options.scaleTilesZoomGeq) {
                // preload tiles for level this.options.scaleTilesZoomGeq - 1
                // how many zoom levels away from this.options.scaleTilesZoomGeq - 1?
                const no_zlevels = zoom - this.options.scaleTilesZoomGeq + 1;
                // strip as many digits from quadkey
                quadkey = quadkey.slice(0, -no_zlevels);
            }
            this.loadTile(quadkey, scheme, callback, callback);
        });
    }
    /**
     * Load a tile image into memory.
     * @param quadkey - The quadkey.
     * @param urlscheme - The URL scheme.
     * @param resolve - The resolve callback.
     * @param reject - The reject callback.
     */
    loadTile(quadkey, urlscheme, resolve, reject) {
        if (typeof resolve === 'undefined') {
            resolve = null;
        }
        if (typeof reject === 'undefined') {
            reject = null;
        }
        // string to be used as key in this._images.loaded.
        // We don't use the url because the protocol part may be missing in the request URL
        // (protocol-relative URLs) or may change due to http redirection.
        const cache_key = quadkey + '_' + urlscheme;
        // Is image already in memory?
        if (cache_key in this.images.loaded) {
            const image = this.images.loaded[cache_key];
            if (resolve !== null) {
                resolve(image, true);
            }
            return;
        }
        // Do not try to load tiles that have failed previously.
        if (cache_key in this.images.failed) {
            if (reject !== null) {
                reject(null, true);
            }
            return;
        }
        // Is image currently being loaded?
        if (cache_key in this.images.loading) {
            if (resolve !== null) {
                const image = this.images.loading[cache_key];
                image.addEventListener('load', () => {
                    if (resolve) {
                        resolve(image, false);
                    }
                });
            }
            if (reject !== null) {
                const image = this.images.loading[cache_key];
                image.addEventListener('error', () => {
                    if (reject)
                        reject(image, false);
                });
            }
            return;
        }
        // ==> load image into memory
        const image = new Image();
        image.addEventListener('load', () => {
            this.images.loaded[cache_key] = image;
            delete this.images.loading[cache_key];
            if (resolve !== null && resolve !== undefined) {
                resolve(image, false);
            }
        });
        image.addEventListener('error', () => {
            this.images.failed[cache_key] = true;
            delete this.images.loading[cache_key];
            if (reject !== null && reject !== undefined) {
                reject(image, false);
            }
        });
        const coords = key2tile(quadkey);
        const url = L.Util.template(urlscheme, coords);
        this.images.loading[cache_key] = image;
        image.src = url;
    }
    /**
     * Create a tile.
     * @param coords - The coordinates.
     * @returns {HTMLCanvasElement}
     */
    createTile(coords) {
        // create canvas element, same size as tiles
        const size = this.getTileSize();
        const canvas = L.DomUtil.create('canvas', 'leaflet-tile');
        canvas.width = size.x;
        canvas.height = size.y;
        this.drawContent(canvas, coords);
        return canvas;
    }
    /**
     * Tell L.GridLayer that the drawing of a specific (async) tile is ready
     * @param coords - The coordinates.
     * @param error - The error to show in case of an error.
     * @param tile - The tile.
     */
    asyncTileDone(coords, error, tile) {
        // tell GridLayer it is ready to readraw (see done-Argument in GridLayer.createTile)
        this._tileReady(coords, error, tile);
    }
    /**
     * Draw a single tile
     * @param canvas - The canvas to draw on.
     * @param coords - The tile's coordinates.
     */
    drawContent(canvas, coords) {
        const context = canvas.getContext('2d');
        if (!context) {
            return;
        }
        context.clearRect(0, 0, canvas.width, canvas.height);
        // disable smoothing since it causes ugly effects on tile's edges
        context.imageSmoothingEnabled = false;
        context.mozImageSmoothingEnabled = false;
        context.msImageSmoothingEnabled = false;
        const quadkey = tile2key(coords.x, coords.y, coords.z);
        const time = this.time;
        if (time === null)
            return;
        const index = this.timesteps.findIndex((timestep) => timestep.equals(time));
        if (index === -1) {
            return;
        }
        const urlscheme = this.urlSchemes[index];
        // don't draw anything for tiles that are hidden
        const zoom = coords.z;
        if (this.options.hideAfterZoom !== null &&
            zoom > this.options.hideAfterZoom) {
            context.clearRect(0, 0, this.options.tileSize, this.options.tileSize);
            return;
        }
        if (this.options.hideBeforeZoom !== null &&
            zoom < this.options.hideBeforeZoom) {
            context.clearRect(0, 0, this.options.tileSize, this.options.tileSize);
            return;
        }
        if (this.options.scaleTilesZoomGeq === null ||
            coords.z < this.options.scaleTilesZoomGeq) {
            const callback = (imgObj) => {
                // don't display image if time does not match any more, i.e. slider has been
                // moved in the meantime.
                if (!this.timesteps[index].equals(time)) {
                    return;
                }
                context.clearRect(0, 0, this.options.tileSize, this.options.tileSize);
                if (imgObj) {
                    context.drawImage(imgObj, 0, 0, this.options.tileSize, this.options.tileSize);
                }
                this.asyncTileDone(coords, null, context.canvas);
            };
            this.loadTile(quadkey, urlscheme, callback);
        }
        else {
            // apply client side tile scaling...
            // how many zoom levels away from this.options.scaleTilesZoomGeq - 1?
            const no_zlevels = coords.z - this.options.scaleTilesZoomGeq + 1;
            // strip as many digits from quadkey
            const new_quadkey = quadkey.slice(0, -no_zlevels);
            const remaining_digits = quadkey.slice(-no_zlevels);
            const callback = (image) => {
                // don't display image if time does not match any more, i.e. slider has been
                // moved in the meantime.
                if (!this.timesteps[index].equals(time)) {
                    return;
                }
                context.clearRect(0, 0, this.options.tileSize, this.options.tileSize);
                let sx = 0;
                let sy = 0;
                const sWidth = Math.round(this.options.tileSize / Math.pow(2, no_zlevels));
                const sHeight = sWidth;
                for (let i = 0; i < remaining_digits.length; i++) {
                    const delta_px = Math.round(this.options.tileSize / Math.pow(2, i + 1));
                    const digit = remaining_digits.charAt(i);
                    // recall that digit denotes one of the four segments:
                    //  ---------
                    //  | 0 | 1 |
                    //  ---------
                    //  | 2 | 3 |
                    //  ---------
                    switch (digit) {
                        case '1':
                            sx = sx + delta_px;
                            break;
                        case '2':
                            sy = sy + delta_px;
                            break;
                        case '3':
                            sx = sx + delta_px;
                            sy = sy + delta_px;
                            break;
                    }
                }
                if (image) {
                    context.drawImage(image, 
                    // x, y and width, height in source tile
                    sx, sy, sWidth, sHeight, 
                    // x, y and width, height in destination tile
                    0, 0, this.options.tileSize, this.options.tileSize);
                }
                this.asyncTileDone(coords, null, context.canvas);
            };
            this.loadTile(new_quadkey, urlscheme, callback);
        }
    }
}
//# sourceMappingURL=LayerGridAPI.js.map